import React, { useState, useEffect, useMemo } from 'react';
import { AvatarSprite } from './AvatarSprite';
import { DialogueTextBox } from './DialogueTextBox';
import { CompanyProfileStep, CompanyDescriptionOverlay } from './CompanyProfileStep';
import { UserModal } from '../ui/modals/UserModal';
import { useAuth } from '../../hooks/useAuth';
import { resolveTiers } from '../../config/metricGroups';
import type { BaronWuffettGesture } from '../../config/avatars';
import type { StockAnalysis, AnalysisSource } from '../../hooks/useAnalysis';

const APRIL_FOOLS_DATE = '2026-04-01';

interface BaronWuffettDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onStepChange?: (stepKey: string | null) => void;
  analysis: StockAnalysis | null;
  isAnalysisLoading: boolean;
  category?: string;
  selectedGroups?: [string, string] | null;
  onDialogueOpen?: () => void;
  gameDate?: string;
  logoUrl?: string;
}

interface StepConfig {
  key: string;
  title?: string;
  gesture: BaronWuffettGesture;
  buttonLabel: string;
}

function buildSteps(selectedGroups?: [string, string] | null, hasCompanyProfile?: boolean): StepConfig[] {
  const { tierLabels } = resolveTiers(selectedGroups);
  const steps: StepConfig[] = [
    { key: 'intro', gesture: 'baron-wuffett-smile', buttonLabel: "Let's Go!" },
  ];
  if (hasCompanyProfile) {
    steps.push({ key: 'companyProfile', title: 'Company Profile', gesture: 'baron-wuffett-point-up', buttonLabel: 'Next' });
  }
  steps.push(
    { key: 'priceChart', title: 'Price Chart', gesture: 'baron-wuffett-reading', buttonLabel: 'Next' },
    { key: 'group1Analysis', title: tierLabels[0], gesture: 'baron-wuffett-reading', buttonLabel: 'Next' },
    { key: 'group2Analysis', title: tierLabels[1], gesture: 'baron-wuffett-ponder', buttonLabel: 'Next' },
    { key: 'group3Analysis', title: tierLabels[2], gesture: 'baron-wuffett-ponder', buttonLabel: 'Next' },
    { key: 'group4Analysis', title: tierLabels[3], gesture: 'baron-wuffett-reading', buttonLabel: 'Finish' },
  );
  return steps;
}

function buildAprilFoolsSteps(): StepConfig[] {
  return [
    { key: 'intro', gesture: 'baron-wuffett-smile', buttonLabel: "Let's Go!" },
    { key: 'priceChart', title: 'The Origins', gesture: 'baron-wuffett-reading', buttonLabel: 'Next' },
    { key: 'group1Analysis', title: 'The Rise', gesture: 'baron-wuffett-reading', buttonLabel: 'Next' },
    { key: 'group2Analysis', title: 'The Scandal', gesture: 'baron-wuffett-ponder', buttonLabel: 'Next' },
    { key: 'group3Analysis', title: 'The Fall', gesture: 'baron-wuffett-ponder', buttonLabel: 'Next' },
    { key: 'group4Analysis', title: 'The Aftermath', gesture: 'baron-wuffett-reading', buttonLabel: 'Finish' },
  ];
}

const IntroText = () => (
  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
    Hello, I'm Baron Wuffett. My philosophy is simple:{' '}
    <strong>invest for the long term in wonderful businesses with strong fundamentals, purchased at fair prices, rather than chasing mediocre companies just because they look cheap.</strong>{' '}
    Together, we'll break down this stock objectively, focusing on business quality, financial health, and long-term potential. Let's get started.
  </p>
);

const AprilFoolsIntroText = () => (
  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
    Welcome to the <strong>April Fools Special</strong>. Today, instead of analyzing a thriving business, we're revisiting one of the market's most dramatic cautionary tales.{' '}
    Every great investor studies failure as carefully as success — perhaps more so. Let me walk you through what happened, from the promising beginnings to the inevitable reckoning.
  </p>
);

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-2">
    {title}
  </h3>
);

const BodyText: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
    {text}
  </p>
);

const MAX_SOURCE_LINKS = 4;

function sourceLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Clickable links to the web sources behind the analysis */
const SourceLinks: React.FC<{ sources: AnalysisSource[] }> = ({ sources }) => (
  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
    Sources:{' '}
    {sources.slice(0, MAX_SOURCE_LINKS).map((source, i) => (
      <React.Fragment key={source.url}>
        {i > 0 && ' · '}
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          title={source.title}
          className="underline hover:text-amber-600 dark:hover:text-amber-400"
        >
          {sourceLabel(source.url)}
        </a>
      </React.Fragment>
    ))}
  </p>
);

export const BaronWuffettDialogue: React.FC<BaronWuffettDialogueProps> = ({
  isOpen,
  onClose,
  onStepChange,
  analysis,
  isAnalysisLoading,
  category,
  selectedGroups,
  onDialogueOpen,
  gameDate,
  logoUrl,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useAuth();

  const isAprilFools = gameDate === APRIL_FOOLS_DATE;
  const hasCompanyProfile = !!analysis?.companyProfile;
  const steps = useMemo(
    () => isAprilFools ? buildAprilFoolsSteps() : buildSteps(selectedGroups, hasCompanyProfile),
    [selectedGroups, isAprilFools, hasCompanyProfile]
  );

  // Notify both parent state and direct listeners (MobileMetricCards)
  const emitStepChange = (stepKey: string | null) => {
    onStepChange?.(stepKey);
    window.dispatchEvent(new CustomEvent('baron-spotlight-step', { detail: stepKey }));
  };

  // Reset step when dialogue opens, notify parent, and retry click recording
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      emitStepChange('intro');
      onDialogueOpen?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const step = steps[stepIndex];
  const isIntro = stepIndex === 0;
  const needsAnalysis = stepIndex > 0;

  const handleAction = () => {
    if (stepIndex === steps.length - 1) {
      emitStepChange(null);
      onClose();
    } else if (stepIndex === 0 && !user && category !== 'popular') {
      setShowAuthModal(true);
    } else {
      const nextIndex = stepIndex + 1;
      setStepIndex(nextIndex);
      emitStepChange(steps[nextIndex].key);
    }
  };

  // After intro, if analysis isn't available, show fallback
  if (needsAnalysis && !analysis) {
    const handleFallbackClose = () => {
      emitStepChange(null);
      onClose();
    };
    return (
      <>
        <DialogueTextBox
          text={isAnalysisLoading ? 'Loading analysis...' : 'Analysis is not available for this stock yet. Check back later!'}
          actionLabel={isAnalysisLoading ? 'Loading...' : 'Close'}
          actionDisabled={isAnalysisLoading}
          onAction={handleFallbackClose}
          onClose={handleFallbackClose}
          avatar={<AvatarSprite gesture="baron-wuffett-ponder" size={120} />}
        />
        <UserModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  // Render step content
  const renderContent = () => {
    if (isIntro) {
      return isAprilFools ? <AprilFoolsIntroText /> : <IntroText />;
    }

    if (!analysis) return undefined;

    const { key, title } = step;

    // Company profile step — whatTheyDo summary in dialogue; description shown as overlay
    if (key === 'companyProfile' && analysis.companyProfile) {
      return <CompanyProfileStep whatTheyDo={analysis.companyProfile.whatTheyDo} />;
    }

    // Plain text steps
    if (key === 'priceChart' || key === 'group1Analysis' || key === 'group2Analysis' || key === 'group3Analysis' || key === 'group4Analysis') {
      const text = analysis[key as keyof Pick<StockAnalysis, 'priceChart' | 'group1Analysis' | 'group2Analysis' | 'group3Analysis' | 'group4Analysis'>];
      return (
        <>
          {title && <SectionTitle title={title} />}
          <BodyText text={text} />
          {analysis.sources && analysis.sources.length > 0 && <SourceLinks sources={analysis.sources} />}
        </>
      );
    }

    return undefined;
  };

  const content = renderContent();

  const resolvedLogoUrl = analysis?.companyProfile?.logoUrl || logoUrl;
  const showLogo = step.key === 'companyProfile' && !!resolvedLogoUrl;

  return (
    <>
      {showLogo && (
        <CompanyDescriptionOverlay logoUrl={resolvedLogoUrl} />
      )}
      <DialogueTextBox
        actionLabel={step.buttonLabel}
        onAction={handleAction}
        onClose={onClose}
        avatar={<AvatarSprite gesture={step.gesture} size={120} />}
      >
        {content}
      </DialogueTextBox>
      <UserModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};
